import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyScanUsers } from './companyScanApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Scans section.
 *
 * One column per scan plus a "Types" column, because the two say different
 * things: six scans all of the same kind is somebody rechecking one worry, and
 * three spread across the three is somebody using the feature as intended.
 * Sorting is by total scans, then by breadth as the tie-break.
 *
 * Every user of the population is listed, including those who have never
 * scanned — they show dashes rather than zeros, so "hasn't used it" reads
 * differently from "used it once and stopped".
 *
 * Counts and a date. No row here carries a scan result of any kind.
 */
const ScanUsersTable = ({
  fetcher = fetchCompanyScanUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const active = row.totalScans > 0;
          const cell = (value) => (active ? Number(value || 0).toLocaleString() : '-');
          return {
            ...row,
            totalText: cell(row.totalScans),
            typesText: active ? `${row.typesUsed} of 3` : '-',
            skinText: cell(row.skin),
            teethText: cell(row.teeth),
            ageText: cell(row.age),
            lastScanText: row.lastScanDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('ScanUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Scans', key: 'totalText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Types', key: 'typesText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Skin', key: 'skinText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Teeth', key: 'teethText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Age', key: 'ageText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Last scan', key: 'lastScanText', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

ScanUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default ScanUsersTable;
